import { z } from "zod";
import { UIElementType } from "../../../../AppConstants";
import { t } from "i18next";

const getConsumableProductSchema = () => {
  return z.object({
    LongName: z
      .string()
      .min(1, { message: "Long name is required" })
      .describe(JSON.stringify({ elementType: UIElementType.TEXTFIELD, inputLabel: t("consumableProductList.longName"), isRequired: true })),
    ShortName: z
      .string()
      .min(1, { message: "Short name is required" })
      .describe(JSON.stringify({ elementType: UIElementType.TEXTFIELD, inputLabel: t("consumableProductList.shortName"), isRequired: true })),
    MeasurementUnit: z
      .string()
      .describe(
        JSON.stringify({
          elementType: UIElementType.TEXTFIELD,
          inputLabel: t("consumableProductList.measurementUnit"),
          isRequired: false,
        })
      )
      .regex(/^[a-zA-Z0-9]*$/, "Special characters are not allowed"),
    ContractCost: z.union([z.literal(0), z.number().positive()])
      .nullable()
      .refine(value => value === null || value >= 0, {
        message: "If entered, the number must be positive or zero",
      }).describe(
        JSON.stringify({
          inputType: "number",
          elementType: UIElementType.TEXTFIELD,
          inputLabel: t("consumableProductList.contractCost"),
          isRequired: false,
        })
      ),
    ICABSCode: z
      .string()
      .max(10, "Maximum of 10 characters allowed")
      .describe(JSON.stringify({ elementType: UIElementType.TEXTFIELD, inputLabel: t("consumableProductList.icabsCode"), isRequired: false })),
    Toxic: z
      .string()
      .describe(JSON.stringify({ elementType: UIElementType.RADIOBUTTON, inputLabel: t("consumableProductList.toxic"), isRequired: false }))
      .transform((val) => {
        console.log(val);
        if (typeof val !== "string") return val; // If it's not a string, return as is
        return val.toLowerCase() === "true";
      }),
    ApplicationUnit: z
      .string()
      .describe(
        JSON.stringify({
          elementType: UIElementType.TEXTFIELD,
          inputLabel: t("consumableProductList.applicationUnit"),
          isRequired: false
        })
      )
      .regex(/^[a-zA-Z0-9]*$/, "Special characters are not allowed"),
    ApplicationUnitCost: z.union([z.literal(0), z.number().positive()])
      .nullable()
      .refine(value => value === null || value >= 0, {
        message: "If entered, the number must be positive or zero",
      }).describe(
        JSON.stringify({
          inputType: "number",
          elementType: UIElementType.TEXTFIELD,
          inputLabel: t("consumableProductList.applicationUnitCost"),
          isRequired: false,
        })
      ),
    GMMaterial: z.union([z.literal(0), z.number().positive()])
      .nullable()
      .refine(value => value === null || value >= 0, {
        message: "If entered, the number must be positive or zero",
      }).describe(
        JSON.stringify({
          inputType: "number",
          elementType: UIElementType.TEXTFIELD,
          inputLabel: t("consumableProductList.grossMargin"),
          isRequired: false,
        })
      ),
  });
};

const getConsumableProductDefaultValue = (value = null) => {
  return {
    LongName: value && value.LongName ? value.LongName : "",
    ShortName: value && value.ShortName ? value.ShortName : "",
    MeasurementUnit: value && value.MeasurementUnit ? value.MeasurementUnit : "",
    ContractCost: value && value.ContractCost ? value.ContractCost : 0,
    ICABSCode: value && value.ICABSCode ? value.ICABSCode : "",
    Toxic: value ? (value?.Toxic === true || value?.Toxic === "true" ? "true" : "false") : "false",
    ApplicationUnit: value && value.ApplicationUnit ? value.ApplicationUnit : "",
    ApplicationUnitCost: value && value.ApplicationUnitCost ? value.ApplicationUnitCost : 0,
    GMMaterial: value && value.GMMaterial ? value.GMMaterial : 0,
  };
};

const getConsumableProductAPIPostSchema = (countryCode, businessCode) => {
  return {
    countryCode: countryCode,
    businessCode: businessCode,
    consumableproduct: [
      {
        ConsumableProductId: "",
        LongName: null,
        MeasurementUnit: null,
        ContractCost: 0,
        ICABSCode: "",
        Toxic: false,
        ApplicationUnit: "",
        ApplicationUnitCost: "",
        GMMaterial: "",
        replacementProduct: [
          {
            StartFrequencyReposition: "",
            ReplaceProductId: "",
            EndFrequencyReposition: "",
            MaintenancePercentage: "",
          },
        ],
        Deleted: false,
        CountryCode: countryCode,
        BusinessCode: businessCode,
      },
    ],
  };
};

const getConsumableProductAuditInfoSchema = () => {
  return {
    auditInfo: [
      {
        Location: "Consumable Product",
        LocationKey: "",
        Action: null,
        shortDesc: null,
        currentState: null,
        previousState: null,
      },
    ],
  };
};

export {
  getConsumableProductSchema,
  getConsumableProductDefaultValue,
  getConsumableProductAPIPostSchema,
  getConsumableProductAuditInfoSchema,
};
